import { t } from '@lingui/core/macro';
import { useMemo } from 'react';

import type { ScreenerResponseStatus } from '@/api/core-api/types.gen';
import type { ProgressIconType } from '@/components/base/progress-steps/progress-types';

import { ScreenerResponseStatus as Status } from '@/api/core-api/types.gen';
import { Progress } from '@/components/base/progress-steps/progress-steps';
import { isModeratedStudy as isModeratedStudyFn } from '@common/utils';

const MODERATED_STAGES: {
  status: ScreenerResponseStatus;
  title: string;
  description: string;
}[] = [
  { status: Status.INVITED, title: t`Invited`, description: '' },
  { status: Status.SCHEDULED, title: t`Scheduled`, description: '' },
  { status: Status.ATTENDED, title: t`Completed`, description: '' },
  { status: Status.PAID, title: t`Paid`, description: '' },
];

const UNMODERATED_STAGES: {
  status: ScreenerResponseStatus;
  title: string;
  description: string;
}[] = [
  { status: Status.INVITED, title: t`Invited`, description: '' },
  { status: Status.ATTENDED, title: t`Completed`, description: '' },
  { status: Status.PAID, title: t`Paid`, description: '' },
];

const AI_MOD_STAGES: {
  status: ScreenerResponseStatus;
  title: string;
  description: string;
}[] = [
  { status: Status.INVITED, title: t`Invited`, description: '' },
  { status: Status.ATTENDED, title: t`Completed`, description: '' },
  { status: Status.PAID, title: t`Paid`, description: '' },
];

const STATUS_ORDER: ScreenerResponseStatus[] = [
  Status.INVITED,
  Status.SCHEDULED,
  Status.ATTENDED,
  Status.PAID,
];

/**
 * Map current status to step display: current stage and all previous stages
 * show as complete (green); the next stage (if any) shows as current.
 */
function statusToStepStatus(
  currentStatus: ScreenerResponseStatus,
  stageStatus: ScreenerResponseStatus,
): 'incomplete' | 'current' | 'complete' {
  const currentIndex = STATUS_ORDER.indexOf(currentStatus);
  const stageIndex = STATUS_ORDER.indexOf(stageStatus);
  if (currentIndex === -1 || stageIndex === -1) return 'incomplete';
  if (stageIndex <= currentIndex) return 'complete';
  if (stageIndex === currentIndex + 1) return 'current';
  return 'incomplete';
}

export const APPLICABLE_STATUSES: ScreenerResponseStatus[] = [
  Status.INVITED,
  Status.SCHEDULED,
  Status.ATTENDED,
  Status.PAID,
];

export function isStatusShowingProgress(
  status: ScreenerResponseStatus,
): boolean {
  return APPLICABLE_STATUSES.includes(status);
}

interface ProjectStatusProgressStepsProps {
  status: ScreenerResponseStatus;
  kindOfResearch?: number;
  className?: string;
}

/**
 * Progress steps for project application flow: Invited → Scheduled → Attended → Paid.
 * Only render when status is one of these four.
 */
export function ProjectStatusProgressSteps({
  status,
  kindOfResearch,
  className,
}: ProjectStatusProgressStepsProps) {
  const items: ProgressIconType[] = useMemo(() => {
    if (!kindOfResearch) return [];
    const isAiMod = kindOfResearch === 12;
    const isModeratedStudy = isModeratedStudyFn({
      project: { kindOfResearch },
    });
    const stages = isAiMod
      ? AI_MOD_STAGES
      : isModeratedStudy
        ? MODERATED_STAGES
        : UNMODERATED_STAGES;
    return stages.map((stage) => ({
      title: stage.title,
      description: stage.description,
      status: statusToStepStatus(status, stage.status),
    }));
  }, [kindOfResearch, status]);

  if (!isStatusShowingProgress(status) || !kindOfResearch) {
    return null;
  }

  return (
    <div className={className}>
      <Progress.IconsWithText
        type="number"
        items={items}
        size="sm"
        orientation="horizontal"
      />
    </div>
  );
}
